/**
 * Sign-in screen. Which surface it shows is the BACKEND's decision:
 *
 *   local → email + password against /api/auth/login
 *   oidc  → a single button that hands the browser to the identity provider;
 *           no credential ever reaches this application
 *
 * On success the auth context holds the token and the router lands the user on
 * wherever they were headed (or the dashboard).
 */
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Link, TextField, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/LoginOutlined";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./useAuth";
import { ApiError } from "../lib/api";

export function LoginPage() {
  const { t } = useTranslation();
  const { login, mode, signInWithProvider, configError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      void navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t("auth.genericError"),
      );
      setBusy(false);
    }
  };

  // ---- AUTH_MODE=oidc: one button, no form ------------------------------
  if (mode === "oidc") {
    const startSso = async () => {
      setError(null);
      setBusy(true);
      try {
        await signInWithProvider();   // navigates away; nothing follows
      } catch (err) {
        setError(err instanceof Error && err.message ? err.message : t("auth.genericError"));
        setBusy(false);
      }
    };
    return (
      <AuthLayout>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("auth.loginTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("auth.ssoSubtitle")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* A missing server-side configuration is stated plainly: without
              it the button cannot work, and a silent failure would read as
              "the application is broken". */}
          {configError && <Alert severity="warning">{configError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={startSso}
            disabled={busy || Boolean(configError)}
          >
            {busy ? t("auth.signingIn") : t("auth.ssoButton")}
          </Button>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t("auth.ssoNote")}
          </Typography>
        </Box>
      </AuthLayout>
    );
  }

  // ---- AUTH_MODE=local: the platform's own accounts ---------------------
  return (
    <AuthLayout>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("auth.loginTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("auth.noAccount")}{" "}
          <Link component={RouterLink} to="/register" fontWeight={600}>
            {t("auth.requestAccess")}
          </Link>
        </Typography>
      </Box>

      <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          autoFocus
          fullWidth
        />
        <TextField
          label={t("auth.password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          fullWidth
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={busy || !email || !password}
        >
          {busy ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("auth.approvalNote")}
        </Typography>
      </Box>
    </AuthLayout>
  );
}
